import { useCallback, useMemo, useState } from 'react';

import { Nullable } from 'v2/types';

interface CursorPaginationState {
  resetKey: string;
  cursors: Nullable<string>[];
}

export interface ICursorPagination {
  cursor: Nullable<string>;
  page: number;
  hasPrev: boolean;
  onNext: (nextCursor: string) => void;
  onPrev: VoidFunction;
}

const FIRST_PAGE_CURSORS: Nullable<string>[] = [null];

// Keeps a stack of cursors of already opened pages. The stack is dropped whenever `resetKey` changes,
// so the first page is requested in the same render as the new filter, without an extra request.
export function useCursorPagination(resetKey: string): ICursorPagination {
  const [state, setState] = useState<CursorPaginationState>({ resetKey, cursors: FIRST_PAGE_CURSORS });

  const cursors = state.resetKey === resetKey ? state.cursors : FIRST_PAGE_CURSORS;

  const onNext = useCallback(
    (nextCursor: string) => {
      setState({ resetKey, cursors: [...cursors, nextCursor] });
    },
    [resetKey, cursors]
  );

  const onPrev = useCallback(() => {
    setState({ resetKey, cursors: cursors.length > 1 ? cursors.slice(0, -1) : FIRST_PAGE_CURSORS });
  }, [resetKey, cursors]);

  return useMemo(
    () => ({
      cursor: cursors[cursors.length - 1],
      page: cursors.length,
      hasPrev: cursors.length > 1,
      onNext,
      onPrev,
    }),
    [cursors, onNext, onPrev]
  );
}
